<script lang="ts">
  import Icon from "../Icon.svelte";

  interface Props {
    data: {
      number: number;
      hash: string;
      timestamp: number;
      miner: string;
      gasUsed: string;
      gasLimit: string;
      transactions: number;
      baseFeePerGas: string;
    };
  }

  let { data }: Props = $props();

  let timeAgo = $derived.by(() => {
    const seconds = Math.floor(Date.now() / 1000 - data.timestamp);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  });

  let gasUsedM = $derived((parseInt(data.gasUsed) / 1e6).toFixed(2));
  let gasLimitM = $derived((parseInt(data.gasLimit) / 1e6).toFixed(1));
</script>

<div class="block-result fade-in-up">
  <header class="result-header">
    <div class="result-icon">
      <Icon name="cube" size={20} />
    </div>
    <div class="result-title">
      <div class="title-row">
        <h2>Block #{data.number.toLocaleString()}</h2>
        <button class="copy-btn" title="Copy block number">
          <Icon name="copy" size={14} />
        </button>
      </div>
      <span class="result-hash">{data.hash}</span>
    </div>
  </header>

  <div class="stats-grid">
    <div class="stat">
      <div class="stat-icon">
        <Icon name="clock" size={14} />
      </div>
      <div class="stat-content">
        <span class="stat-label">Timestamp</span>
        <span class="stat-value">{timeAgo}</span>
      </div>
    </div>

    <div class="stat">
      <div class="stat-icon">
        <Icon name="layers" size={14} />
      </div>
      <div class="stat-content">
        <span class="stat-label">Transactions</span>
        <span class="stat-value">{data.transactions}</span>
      </div>
    </div>

    <div class="stat">
      <div class="stat-icon">
        <Icon name="fuel" size={14} />
      </div>
      <div class="stat-content">
        <span class="stat-label">Gas Used</span>
        <span class="stat-value">{gasUsedM}M</span>
      </div>
    </div>

    <div class="stat">
      <div class="stat-icon">
        <Icon name="zap" size={14} />
      </div>
      <div class="stat-content">
        <span class="stat-label">Base Fee</span>
        <span class="stat-value">{data.baseFeePerGas}</span>
      </div>
    </div>
  </div>

  <div class="details">
    <div class="detail-row">
      <span class="detail-label">Miner</span>
      <span class="detail-value mono">{data.miner}</span>
    </div>
    <div class="detail-row">
      <span class="detail-label">Gas Limit</span>
      <span class="detail-value">{gasLimitM}M</span>
    </div>
  </div>

  <div class="actions">
    <button class="action-btn primary">
      <Icon name="layers" size={16} />
      View Transactions
    </button>
    <button class="action-btn">
      <Icon name="external-link" size={16} />
      Etherscan
    </button>
  </div>
</div>

<style>
  .block-result {
    background: var(--bg-secondary);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-lg);
    overflow: hidden;
  }

  .result-header {
    display: flex;
    align-items: flex-start;
    gap: var(--space-4);
    padding: var(--space-6);
    border-bottom: 1px solid var(--border-subtle);
  }

  .result-icon {
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1));
    border-radius: var(--radius-md);
    color: var(--accent-primary);
    flex-shrink: 0;
  }

  .result-title {
    flex: 1;
    min-width: 0;
  }

  .title-row {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }

  .title-row h2 {
    font-size: 1.25rem;
    font-weight: 600;
    font-family: var(--font-mono);
    color: var(--text-primary);
  }

  .copy-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: var(--radius-sm);
    color: var(--text-tertiary);
    transition: all var(--transition-fast);
  }

  .copy-btn:hover {
    background: var(--bg-hover);
    color: var(--text-primary);
  }

  .result-hash {
    font-family: var(--font-mono);
    font-size: 0.8125rem;
    color: var(--text-tertiary);
    margin-top: var(--space-1);
    display: block;
  }

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: var(--space-4);
    padding: var(--space-5);
    background: var(--bg-tertiary);
  }

  @media (max-width: 640px) {
    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .stat {
    display: flex;
    align-items: flex-start;
    gap: var(--space-2);
  }

  .stat-icon {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-secondary);
    border-radius: var(--radius-sm);
    color: var(--text-tertiary);
    flex-shrink: 0;
  }

  .stat-content {
    display: flex;
    flex-direction: column;
  }

  .stat-label {
    font-size: 0.6875rem;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .stat-value {
    font-size: 0.9375rem;
    font-weight: 600;
    font-family: var(--font-mono);
    color: var(--text-primary);
    margin-top: 2px;
  }

  .details {
    padding: var(--space-5);
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .detail-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .detail-label {
    font-size: 0.875rem;
    color: var(--text-secondary);
  }

  .detail-value {
    font-size: 0.875rem;
    color: var(--text-primary);
  }

  .detail-value.mono {
    font-family: var(--font-mono);
  }

  .actions {
    display: flex;
    gap: var(--space-3);
    padding: var(--space-5);
    border-top: 1px solid var(--border-subtle);
  }

  .action-btn {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-md);
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-secondary);
    background: var(--bg-tertiary);
    border: 1px solid var(--border-subtle);
    transition: all var(--transition-fast);
  }

  .action-btn:hover {
    border-color: var(--border-default);
    color: var(--text-primary);
  }

  .action-btn.primary {
    background: var(--accent-primary);
    border-color: var(--accent-primary);
    color: white;
  }

  .action-btn.primary:hover {
    background: #2563eb;
  }
</style>
